import { useEffect, useRef, useState, type MouseEvent } from "react";
import { Check, Copy } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";

/**
 * 小型複製按鈕（178 反饋 A17：訂單編號一鍵複製）。
 * - 常放在整卡被 <Link> 包住的列表卡片內，onClick 一律 preventDefault + stopPropagation
 *   擋掉導航（先例 Notifications.tsx 的卡片內按鈕）。
 * - 複製成功後 icon 換 Check 約 2 秒再還原；不引入 toast 系統。
 */
export function CopyButton({
  text,
  label,
  className,
}: {
  /** 要複製到剪貼簿的文字 */
  text: string;
  /** 無障礙標籤（呼叫端以三語 t() 給值）；未給時用通用「複製」三語 */
  label?: string;
  className?: string;
}) {
  const { t } = useLanguage();
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<number | null>(null);

  // 卸載時清掉還原計時器，避免對已卸載元件 setState
  useEffect(
    () => () => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    },
    [],
  );

  const handleCopy = async (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    let ok = false;
    try {
      await navigator.clipboard.writeText(text);
      ok = true;
    } catch {
      // 剪貼簿 API 不可用（非安全來源、權限被拒等）→ 退回暫時 textarea + execCommand
      try {
        const textarea = document.createElement("textarea");
        textarea.value = text;
        textarea.setAttribute("readonly", "");
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.select();
        ok = document.execCommand("copy");
        document.body.removeChild(textarea);
      } catch {
        ok = false;
      }
    }
    if (ok) {
      setCopied(true);
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(() => setCopied(false), 2000);
    }
  };

  const ariaLabel = copied
    ? t({ "zh-TW": "已複製", "en-US": "Copied", "zh-CN": "已复制" })
    : label ?? t({ "zh-TW": "複製", "en-US": "Copy", "zh-CN": "复制" });

  return (
    <button
      type="button"
      onClick={handleCopy}
      aria-label={ariaLabel}
      title={ariaLabel}
      className={`inline-flex h-5 w-5 shrink-0 items-center justify-center rounded text-muted-foreground/60 transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${className ?? ""}`}
    >
      {copied ? <Check className="h-3 w-3 text-primary" /> : <Copy className="h-3 w-3" />}
    </button>
  );
}
